import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, fmtMXN, fmtDate, BACKEND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBar } from "@/components/StatusBar";
import { Chatter } from "@/components/Chatter";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, ExternalLink, FileDown, Truck, FileText, Receipt, Plus, Trash2, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const INV_LABEL = { borrador: "Borrador", enviada: "Enviada (pend. pago)", pagada: "Pagada" };

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [estatus, setEstatus] = useState("");
  const [abono, setAbono] = useState("");
  const [shipOpen, setShipOpen] = useState(false);
  const [shipItems, setShipItems] = useState({});
  const [guias, setGuias] = useState([{ paqueteria: "DHL", guia: "", url_rastreo: "" }]);
  const [shipNota, setShipNota] = useState("");
  const [shipConfirm, setShipConfirm] = useState(false);
  const [shipAcuse, setShipAcuse] = useState(true);
  const [fechaRecibido, setFechaRecibido] = useState("");
  const [acuseEntrega, setAcuseEntrega] = useState(true);

  const load = () => api.get(`/orders/${id}`).then((r) => { setO(r.data); setEstatus(r.data.estatus); });
  useEffect(() => { load(); }, [id]);

  if (!o) return <div className="p-8 text-muted-foreground">Cargando pedido...</div>;

  const changeStatus = async () => {
    try {
      const payload = { estatus };
      if (estatus === "cerrado") { payload.fecha_recibido = fechaRecibido; payload.adjuntar_acuse_entrega = acuseEntrega; }
      await api.put(`/orders/${id}/status`, payload);
      toast.success("Estatus actualizado y correo enviado al cliente");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addAbono = async () => {
    if (!abono) return;
    await api.post(`/orders/${id}/payment`, { monto: Number(abono) });
    toast.success("Abono registrado"); setAbono(""); load();
  };

  const saldo = o.total - (o.cobrado || 0);
  const ship = (o.direccion_envio && Object.keys(o.direccion_envio).length) ? o.direccion_envio : (o.client_snapshot?.direccion_envio || o.client_snapshot?.direccion_facturacion || {});
  const shipLine1 = `${ship.calle || ""} ${ship.numero || ""}${ship.colonia ? `, Col. ${ship.colonia}` : ""}`;
  const shipLine2 = `${ship.cp ? `CP ${ship.cp}, ` : ""}${ship.ciudad || ""} ${ship.estado || ""}`;

  const resumen = o.envio_resumen || { items: [], total_pendiente: 0, completo: false, num_embarques: 0 };
  const pendientes = resumen.items.filter((r) => r.pendiente > 0);
  const embarques = o.embarques || [];
  const openShip = () => {
    const init = {};
    pendientes.forEach((r) => { init[r.sku || r.nombre] = r.pendiente; });
    setShipItems(init);
    setGuias([{ paqueteria: "DHL", guia: "", url_rastreo: "" }]);
    setShipNota(""); setShipConfirm(false); setShipAcuse(true); setShipOpen(true);
  };
  const setGuia = (i, k, v) => setGuias((g) => g.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addGuia = () => setGuias((g) => [...g, { paqueteria: "DHL", guia: "", url_rastreo: "" }]);
  const delGuia = (i) => setGuias((g) => (g.length > 1 ? g.filter((_, idx) => idx !== i) : g));
  const submitShip = async () => {
    const items = pendientes.map((r) => ({ sku: r.sku, nombre: r.nombre, cantidad: Number(shipItems[r.sku || r.nombre] || 0) })).filter((x) => x.cantidad > 0);
    if (!items.length) return toast.error("Indica la cantidad a enviar en al menos un ítem");
    const gs = guias.filter((g) => g.guia || g.url_rastreo);
    if (!gs.length) return toast.error("Agrega al menos una guía o URL de rastreo");
    if (!shipConfirm) return toast.error("Confirma la dirección de envío");
    try {
      await api.post(`/orders/${id}/shipments`, { items, guias: gs, nota: shipNota, direccion_confirmada: true, adjuntar_acuse: shipAcuse });
      toast.success("Embarque registrado y cliente notificado");
      setShipOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => nav("/app/pedidos")}><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="text-2xl font-heading font-bold tracking-tight font-mono">{o.folio}</h1>
        <span className="text-sm text-muted-foreground">P.O. Cliente: <span className="font-mono font-medium text-foreground">{o.po_number}</span></span>
        {o.quotation_id && <Button variant="outline" size="sm" onClick={() => nav(`/app/cotizaciones/${o.quotation_id}`)} data-testid="order-view-quote"><FileText className="w-4 h-4 mr-1" /> Ver cotización {o.folio_cotizacion}</Button>}
        {((o.facturas || []).length > 0 || estatus === "cerrado") && <Button variant="outline" size="sm" onClick={() => nav(`/app/facturacion?order=${o.id}`)} data-testid="order-view-billing"><Receipt className="w-4 h-4 mr-1" /> Ver facturación</Button>}
        {o.po_file_id && <a href={`${BACKEND}/api/files/${o.po_file_id}?auth=${localStorage.getItem("wk_token")}`} target="_blank" rel="noreferrer" className="text-interactive text-sm underline flex items-center gap-1"><FileDown className="w-4 h-4" /> P.O. adjunta</a>}
      </div>
      <StatusBar current={estatus} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5 rounded-lg">
            <h3 className="font-heading font-semibold mb-3">Suministros requeridos</h3>
            <Table>
              <TableHeader><TableRow><TableHead>Producto</TableHead><TableHead>Cant.</TableHead><TableHead>Precio</TableHead><TableHead>Compra web</TableHead></TableRow></TableHeader>
              <TableBody>
                {o.items.map((it, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{it.nombre}<div className="text-xs text-muted-foreground font-mono">{it.sku}</div></TableCell>
                    <TableCell>{it.cantidad} {it.unidad}</TableCell>
                    <TableCell>{fmtMXN(it.precio_unitario)}</TableCell>
                    <TableCell className="space-y-1">
                      {[...(it.proveedor_urls || []), ...(it.extra_urls || [])].filter(Boolean).map((u, j) => (
                        <a key={j} href={u} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-interactive underline"><ExternalLink className="w-3 h-3" /> Surtir</a>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end mt-4 text-sm"><div className="w-56 space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMXN(o.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">IVA 16%</span><span>{fmtMXN(o.iva)}</span></div>
              <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span>{fmtMXN(o.total)}</span></div>
            </div></div>
          </Card>

          <Card className="p-5 rounded-lg">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Actualizar estatus</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Estatus</Label>
                <Select value={estatus} onValueChange={setEstatus}>
                  <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aceptada">Aceptada</SelectItem>
                    <SelectItem value="surtido">En Preparación (Surtido)</SelectItem>
                    {estatus === "enviado" && <SelectItem value="enviado" disabled>Enviado (por embarques)</SelectItem>}
                    <SelectItem value="cerrado">Completado / Cerrado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end"><Button onClick={changeStatus} className="w-full" data-testid="order-status-save">Aplicar y notificar</Button></div>
            </div>
            {estatus === "cerrado" && (
              <div className="mt-4 space-y-3 p-3 rounded-md bg-blue-50 border border-blue-200" data-testid="delivery-receipt-block">
                <div>
                  <Label>Fecha de recibido (confirmación del cliente)</Label>
                  <Input type="date" value={fechaRecibido} onChange={(e) => setFechaRecibido(e.target.value)} data-testid="fecha-recibido" className="max-w-xs" />
                </div>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={acuseEntrega} onChange={(e) => setAcuseEntrega(e.target.checked)} className="mt-0.5" data-testid="acuse-entrega-check" />
                  <span>Adjuntar <b>Acuse de Entrega (PDF)</b> al correo de completado<br /><span className="text-xs text-muted-foreground">Resumen de artículos entregados con fecha de envío por ítem y fecha de recibido. Sin precios.</span></span>
                </label>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-3">El envío se gestiona por <b>embarques</b>. Al despachar todo el material el pedido pasa automáticamente a <b>Enviado</b>.</p>
          </Card>

          <Card className="p-5 rounded-lg" data-testid="shipments-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-heading font-semibold flex items-center gap-2"><PackageCheck className="w-4 h-4" /> Embarques / Envíos {resumen.num_embarques > 0 && <Badge variant="secondary">{resumen.num_embarques}</Badge>}</h3>
              {resumen.total_pendiente > 0
                ? <Button size="sm" onClick={openShip} data-testid="new-shipment-btn"><Plus className="w-4 h-4 mr-1" /> Registrar embarque</Button>
                : <Badge variant="outline" className="text-emerald-700 border-emerald-300">Todo enviado</Badge>}
            </div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Pendiente por enviar</div>
            <Table>
              <TableHeader><TableRow><TableHead>Producto</TableHead><TableHead className="text-right">Pedido</TableHead><TableHead className="text-right">Enviado</TableHead><TableHead className="text-right">Pendiente</TableHead></TableRow></TableHeader>
              <TableBody>
                {resumen.items.map((r, i) => (
                  <TableRow key={i} data-testid="ship-pending-row">
                    <TableCell className="font-medium">{r.nombre}<div className="text-xs text-muted-foreground font-mono">{r.sku}</div></TableCell>
                    <TableCell className="text-right">{r.cantidad}</TableCell>
                    <TableCell className="text-right">{r.enviado}</TableCell>
                    <TableCell className={`text-right font-medium ${r.pendiente > 0 ? "text-amber-600" : "text-emerald-600"}`}>{r.pendiente}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {embarques.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Historial de embarques</div>
                {embarques.map((e, i) => (
                  <div key={e.id} className="border rounded-lg p-3 text-sm" data-testid="shipment-row">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Embarque #{i + 1}</span>
                      <span className="text-xs text-muted-foreground">{fmtDate(e.fecha)} · {e.por}</span>
                    </div>
                    <div className="text-xs mt-1">{e.items.map((x) => `${x.cantidad}× ${x.nombre}`).join(", ")}</div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {e.guias.map((g, j) => (g.url_rastreo
                        ? <a key={j} href={g.url_rastreo} target="_blank" rel="noreferrer" className="text-xs border rounded px-2 py-0.5 text-interactive underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {g.guia || "Rastrear"} · {g.paqueteria}</a>
                        : <span key={j} className="text-xs border rounded px-2 py-0.5">{g.guia} · {g.paqueteria}</span>))}
                    </div>
                    {e.nota && <div className="text-xs text-muted-foreground mt-1">Nota: {e.nota}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5 rounded-lg">
            <h3 className="font-heading font-semibold mb-3">Cobranza</h3>
            <div className="text-sm space-y-1 mb-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Cobrado</span><span>{fmtMXN(o.cobrado || 0)}</span></div>
              <div className="flex justify-between font-medium"><span>Saldo</span><span className={saldo > 0 ? "text-red-500" : "text-emerald-600"}>{fmtMXN(saldo)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-muted-foreground">Vence</span><span className={o.vencido ? "text-red-500" : ""}>{fmtDate(o.fecha_vencimiento)}</span></div>
            </div>
            {saldo > 0 && <div className="flex gap-2"><Input type="number" placeholder="Monto abono" value={abono} onChange={(e) => setAbono(e.target.value)} data-testid="abono-input" /><Button onClick={addAbono} data-testid="abono-save">Abonar</Button></div>}
            <div className="mt-3 space-y-1">{(o.abonos || []).map((a) => <div key={a.id} className="text-xs flex justify-between"><span className="text-muted-foreground">{fmtDate(a.fecha)} · {a.por}</span><span>{fmtMXN(a.monto)}</span></div>)}</div>
          </Card>
          {(o.facturas || []).length > 0 && (
            <Card className="p-5 rounded-lg" data-testid="order-invoices">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Facturas emitidas</h3>
              <div className="space-y-2">
                {o.facturas.map((f) => (
                  <div key={f.id} className="text-sm flex justify-between items-center border-b border-border pb-1.5 last:border-0" data-testid="order-invoice-row">
                    <div><span className="font-mono">{f.folio_interno}</span><div className="text-xs text-muted-foreground">{f.metodo_pago} · {INV_LABEL[f.estatus] || f.estatus}{f.is_partial ? " · Parcial" : ""}</div></div>
                    <span className="font-medium">{fmtMXN(f.monto)}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => nav(`/app/facturacion?order=${o.id}`)} className="mt-3 text-xs text-interactive underline flex items-center gap-1" data-testid="order-goto-billing"><Receipt className="w-3.5 h-3.5" /> Ver en Órdenes a Facturar</button>
            </Card>
          )}
            <Card className="p-5 rounded-lg text-sm" data-testid="order-shipping">
              <h3 className="font-heading font-semibold mb-2 flex items-center gap-2"><Truck className="w-4 h-4" /> Dirección de envío</h3>
              <p>{shipLine1}</p>
              <p className="text-muted-foreground">{shipLine2}</p>
              {ship.referencias && <p className="text-muted-foreground">Ref: {ship.referencias}</p>}
            </Card>
          )}
          <Card className="p-5 rounded-lg text-sm">
            <h3 className="font-heading font-semibold mb-2">Datos fiscales</h3>
            <p className="font-medium">{o.client_snapshot?.razon_social}</p>
            <p className="text-muted-foreground">RFC: {o.client_snapshot?.rfc}</p>
            <p className="text-muted-foreground">{o.client_snapshot?.regimen_fiscal}</p>
          </Card>
          <Chatter entityType="pedido" entityId={id} />
        </div>
      </div>

      <Dialog open={shipOpen} onOpenChange={setShipOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Registrar embarque · <span className="font-mono">{o.folio}</span></DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="border rounded-lg divide-y" data-testid="ship-items">
              <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/40 grid grid-cols-12 gap-2"><span className="col-span-6">Ítem</span><span className="col-span-3 text-right">Pendiente</span><span className="col-span-3 text-right">A enviar</span></div>
              {pendientes.map((r, idx) => { const k = r.sku || r.nombre; return (
                <div key={idx} className="px-3 py-2 grid grid-cols-12 gap-2 items-center text-sm">
                  <span className="col-span-6">{r.nombre}</span>
                  <span className="col-span-3 text-right">{r.pendiente} {r.unidad}</span>
                  <span className="col-span-3"><Input type="number" min={0} max={r.pendiente} value={shipItems[k] ?? ""} onChange={(e) => setShipItems((s) => ({ ...s, [k]: e.target.value }))} data-testid={`ship-qty-${idx}`} className="h-8 text-right" /></span>
                </div>
              ); })}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label>Guías de rastreo</Label><Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={addGuia} data-testid="add-guia"><Plus className="w-3.5 h-3.5 mr-1" /> Agregar guía</Button></div>
              <div className="space-y-2">
                {guias.map((g, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`guia-row-${i}`}>
                    <div className="col-span-3">
                      <Select value={g.paqueteria} onValueChange={(v) => setGuia(i, "paqueteria", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{["DHL", "FedEx", "Estafeta", "UPS", "Paquetexpress", "Otro"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input className="col-span-4 h-8 text-xs" placeholder="N° de guía" value={g.guia} onChange={(e) => setGuia(i, "guia", e.target.value)} data-testid={`guia-num-${i}`} />
                    <Input className="col-span-4 h-8 text-xs" placeholder="URL rastreo" value={g.url_rastreo} onChange={(e) => setGuia(i, "url_rastreo", e.target.value)} data-testid={`guia-url-${i}`} />
                    <button type="button" className="col-span-1 flex justify-center text-red-500" onClick={() => delGuia(i)}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div><Label>Nota (opcional)</Label><Textarea rows={2} value={shipNota} onChange={(e) => setShipNota(e.target.value)} placeholder="Ej. Se envían 2 de 3 cajas; falta 1 por disponibilidad" data-testid="ship-nota" /></div>
            <label className="flex items-start gap-2 text-sm p-3 rounded-md bg-blue-50 border border-blue-200 cursor-pointer">
              <input type="checkbox" checked={shipAcuse} onChange={(e) => setShipAcuse(e.target.checked)} className="mt-0.5" data-testid="ship-acuse" />
              <span>Adjuntar <b>Acuse de Embarque (PDF)</b> al correo del cliente<br /><span className="text-xs text-muted-foreground">Incluye solo los ítems enviados y las guías de rastreo, sin precios.</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm p-3 rounded-md bg-amber-50 border border-amber-300 cursor-pointer">
              <input type="checkbox" checked={shipConfirm} onChange={(e) => setShipConfirm(e.target.checked)} className="mt-0.5" data-testid="ship-confirm" />
              <span>Confirmo que la dirección de envío es correcta:<br /><b>{shipLine1}</b> · {shipLine2}</span>
            </label>
          </div>
          <DialogFooter><Button onClick={submitShip} data-testid="ship-submit"><PackageCheck className="w-4 h-4 mr-1" /> Registrar embarque y notificar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
